import React, { useEffect, useState } from "react";
import { Grid, Box, Button } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { OutletDetailsType } from "../../../features/createSecondaryOrder/CreateSecondaryOrderType";
import {
  CustomDateField,
  CustomAutocompleteField,
  CustomCostField,
  CustomTextAreaField,
  CustomFileUploadField,
  CustomBannerNameField,
  CustomSKUSelect
} from "./DisplayVisibilityCustomFields";
import { popupType } from "../../../types/types";
import { GenericPopUp } from "../../popup/genericPopUp";
import { DisplayVisibilityFormData } from "../../../features/claimsNew/utils/utils";
import { ActionMeta, SingleValue, MultiValue } from 'react-select';
import { formatDecimal } from "../../../components/dms_common/DmsUtils";

interface DisplayVisibilityFormProps {
  onSave: (data: DisplayVisibilityFormData, proceed: boolean) => void;
  initialData?: DisplayVisibilityFormData | null;
  skuProductDescriptionMap: string[];
  customersInfo: OutletDetailsType[];
  currencySymbol: string;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

const DisplayVisibilityForm: React.FC<DisplayVisibilityFormProps> = ({
  onSave,
  initialData = null,
  skuProductDescriptionMap,
  customersInfo,
  currencySymbol,
  setHasUnsavedChanges
}) => {
  const [formData, setFormData] = useState<DisplayVisibilityFormData>({
    id: Date.now().toString(),
    claimDate: dayjs(),
    outletCode: "",
    outletName: "",
    skus: [],
    displayStartDate: dayjs(),
    displayEndDate: dayjs(),
    installationCost: 0,
    displayCost: 0,
    remarks: "",
    blobUrls: [],
    totalClaimAmount: 0,
    bannerName: ""
  });

  const [genericModalState, setGenericModalState] = useState(false);
  const [genericModalMessage, setGenericModalSMessage] = useState("");
  const [genericModaltype, setGenericModalType] = useState<popupType>("Error");
  const [errors, setErrors] = useState<Record<string, boolean>>({
    outletCode: false,
    outletName: false,
    bannerName: false,
    displayStartDate: false,
    displayEndDate: false,
    installationCost: false,
    remarks: false,
    blobUrls: false,
  });

  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({
    outletCode: "",
    outletName: "",
    bannerName: "",
    displayStartDate: "",
    displayEndDate: "",
    installationCost: "",
    remarks: "",
    blobUrls: "",
  });
  const skuOptions = skuProductDescriptionMap.map((skuDescription) => ({
    value: skuDescription.split(" - ")[0],
    label: skuDescription,
  }));

  const openPopUp = (message: string, modalType: popupType) => {
    setGenericModalSMessage(message);
    setGenericModalState(true);
    setGenericModalType(modalType);
  };
  const isSingleValue = (
    value: SingleValue<{ value: string; label: string }> | MultiValue<{ value: string; label: string }>
  ): value is SingleValue<{ value: string; label: string }> => {
    return !Array.isArray(value);
  };
  const handleSKUChange = (
    index: number,
    newValue: SingleValue<{ value: string; label: string }> | MultiValue<{ value: string; label: string }>,
    actionMeta: ActionMeta<{ value: string; label: string }>
  ) => {
    const newSkus = [...formData.skus];

    if (newValue && isSingleValue(newValue)) {
      const [skuCode, skuName] = newValue.label.split(" - ");
      newSkus[index] = { skuCode, skuName };
    } else {
      newSkus.splice(index, 1);
    }

    setFormData({ ...formData, skus: newSkus });
  };

  const addMoreSKUs = () => {
    if (formData.skus.length < 2 || !formData.skus[0]?.skuCode || !formData.skus[1]?.skuCode) {
      openPopUp("Fill the first two SKU fields before adding more SKUs.", "Alert");
      return;
    }
  
    if (formData.skus.length > 2 && !formData.skus[formData.skus.length - 1]?.skuCode) {
      openPopUp("Please fill the current SKU field before adding more SKUs.", "Alert");
      return;
    }
  
    setFormData({ ...formData, skus: [...formData.skus, { skuCode: "", skuName: "" }] });
  };

  const handleOutletChange = (event: React.SyntheticEvent, value: OutletDetailsType | null) => {
    if (value) {
      setFormData((prev) => ({
        ...prev,
        outletCode: value.outletCode || "",
        outletName: value.outletName || "",
      }));

      setErrors((prev) => ({
        ...prev,
        outletCode: false,
        outletName: false,
      }));
      setErrorMessages((prev) => ({
        ...prev,
        outletCode: "",
        outletName: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        outletCode: "",
        outletName: "",
      }));
    }
  };

  const isStartDateIsGreaterThanEndDate = (
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ) => {
    const diff = endDate.diff(startDate);
    return diff < 0;
  };

  const handleDateChange = (name: keyof DisplayVisibilityFormData, newValue: Dayjs | null) => {
    const dateString = newValue ? newValue.format("DD-MM-YYYY") : "";
    const isValidFormat = validateDateFormat(dateString);
    if (!isValidFormat) {
      if (name === "displayStartDate" || name === "displayEndDate") {
        setErrors((prev) => ({ ...prev, [name]: true }));
        setErrorMessages((prev) => ({ ...prev, [name]: "Invalid date format." }));
      }
    } else {
      setErrors((prev) => ({ ...prev, [name]: false }));
      setErrorMessages((prev) => ({ ...prev, [name]: "" }));
      setFormData({ ...formData, [name]: newValue });
  
      if (name === "displayStartDate" || name === "displayEndDate") {
        const startDate = name === "displayStartDate" ? newValue : formData.displayStartDate;
        const endDate = name === "displayEndDate" ? newValue : formData.displayEndDate;
  
        if (startDate && endDate) {
          if (isStartDateIsGreaterThanEndDate(startDate, endDate)) {
            if (name === "displayStartDate") {
              setErrors((prev) => ({ ...prev, displayStartDate: true }));
              setErrorMessages((prev) => ({ ...prev, displayStartDate: "Display Start Date must be before Display End Date." }));
            } else if (name === "displayEndDate") {
              setErrors((prev) => ({ ...prev, displayEndDate: true }));
              setErrorMessages((prev) => ({ ...prev, displayEndDate: "Display End Date must be after Display Start Date." }));
            }
          } else {
            setErrors((prev) => ({
              ...prev,
              displayStartDate: false,
              displayEndDate: false,
            }));
            setErrorMessages((prev) => ({
              ...prev,
              displayStartDate: "",
              displayEndDate: "",
            }));
          }
        }
      }
    }
  };

  const handleBannerNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [name]: false }));
      setErrorMessages((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numericValue = value === '' ? 0 : parseFloat(value);
    if (numericValue >= 0) {
      setFormData(prev => ({
        ...prev,
        [name]: numericValue,
        totalClaimAmount: prev.totalClaimAmount + numericValue - prev[name],
      }));
      if (numericValue > 0) {
        setErrors((prev) => ({ ...prev, [name]: false }));
        setErrorMessages((prev) => ({ ...prev, [name]: "" }));
      }
    }
    else
    {
      openPopUp("Negative numbers are not allowed.", "Alert");
      setFormData((prev) => ({
        ...prev,
        [name]: prev[name] || 0, 
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [e.target.name]: false }));
      setErrorMessages((prev) => ({ ...prev, [e.target.name]: "" }));
    }
  };

  const validateOutletCode = () => {
    const isValid = formData.outletCode !== "";
    setErrors((prev) => ({ ...prev, outletCode: !isValid }));
    setErrorMessages((prev) => ({ ...prev, outletCode: isValid ? "" : "Outlet Code is required" }));
    return isValid;
  };

  const validateOutletName = () => {
    const isValid = formData.outletName !== "";
    setErrors((prev) => ({ ...prev, outletName: !isValid }));
    setErrorMessages((prev) => ({ ...prev, outletName: isValid ? "" : "Outlet Name is required" }));
    return isValid;
  };

  const validateBannerName = (): boolean => {
    if (formData.bannerName.trim() === "") {
      setErrors((prev) => ({ ...prev, bannerName: true }));
      setErrorMessages((prev) => ({ ...prev, bannerName: "Display Name is required" }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, bannerName: false }));
      setErrorMessages((prev) => ({ ...prev, bannerName: "" }));
      return true;
    }
  };

  const validateDateFormat = (date: string): boolean => {
    const regex = /^\d{2}-\d{2}-\d{4}$/;
    return regex.test(date);
};

  const validateInstallationCost = (): boolean => {
    if (formData.installationCost <= 0) {
      setErrors((prev) => ({ ...prev, installationCost: true }));
      setErrorMessages((prev) => ({ ...prev, installationCost: "Installation Cost must be greater than 0" }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, installationCost: false }));
      setErrorMessages((prev) => ({ ...prev, installationCost: "" }));
      return true;
    }
  };

  const validateRemarks = (): boolean => {
    if (formData.remarks.trim() === "") {
      setErrors((prev) => ({ ...prev, remarks: true }));
      setErrorMessages((prev) => ({ ...prev, remarks: "Remarks are required" }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, remarks: false }));
      setErrorMessages((prev) => ({ ...prev, remarks: "" }));
      return true;
    }
  };

  const validateBlobUrls = () => {
    const isValid = formData.blobUrls.length > 0;
    setErrors((prev) => ({ ...prev, blobUrls: !isValid }));
    setErrorMessages((prev) => ({ ...prev, blobUrls: isValid ? "" : "Proof of Expense is required" }));
    return isValid;
  };

  const isFormValid = () => {
    return (
      formData.outletCode !== "" &&
      formData.outletName !== "" &&
      formData.installationCost > 0 &&
      formData.remarks !== "" &&
      formData.proofOfExpenseImage !== null &&
      formData.bannerName !== "" &&
      !errors.displayStartDate && 
      !errors.displayEndDate
    );
  };

  const filterValidSKUs = (skus: { skuCode: string; skuName: string }[]) => {
    return skus.filter(sku => sku.skuCode.trim() !== "" && sku.skuName.trim() !== "");
  };

const handleSave = (clearForm: boolean) => {
  const filteredSkus = filterValidSKUs(formData.skus);
  const filteredFormData = { ...formData, skus: filteredSkus };

  const isOutletCodeValid = validateOutletCode();
  const isOutletNameValid = validateOutletName();
  const isBannerNameValid = validateBannerName();
  const isInstallationCostValid = validateInstallationCost();
  const isRemarksValid = validateRemarks();
  const isBlobUrlsValid = validateBlobUrls();

  const isFormValid =
    isOutletCodeValid &&
    isOutletNameValid &&
    isBannerNameValid &&
    isInstallationCostValid &&
    isRemarksValid &&
    isBlobUrlsValid &&
    !errors.displayStartDate &&
    !errors.displayEndDate;

  if (!isFormValid) {
    return;
  }

  onSave(filteredFormData, !clearForm);

  if (clearForm) {
    setFormData({
      id: Date.now().toString(),
      claimDate: dayjs(),
      outletCode: "",
      outletName: "",
      skus: [],
      displayStartDate: dayjs(),
      displayEndDate: dayjs(),
      installationCost: 0,
      displayCost: 0,
      remarks: "",
      blobUrls: [],
      totalClaimAmount: 0,
      bannerName: ""
    });
  }
  setHasUnsavedChanges(false);
};

  useEffect(() => {
    if (initialData) {
      const filteredSkus = filterValidSKUs(initialData.skus || []);

      setFormData({
        id: initialData.id || "",
        claimDate: initialData.claimDate || dayjs(),
        outletCode: initialData.outletCode || "",
        outletName: initialData.outletName || "",
        skus: filteredSkus,
        displayStartDate: initialData.displayStartDate || dayjs(),
        displayEndDate: initialData.displayEndDate || dayjs(),
        installationCost: initialData.installationCost || 0,
        displayCost: initialData.displayCost || 0,
        remarks: initialData.remarks || "",
        blobUrls: initialData.blobUrls || [],
        totalClaimAmount: initialData.totalClaimAmount || 0,
        bannerName: initialData.bannerName || ""
      });
    }
  }, [initialData]);

  useEffect(() => {
    const isPartiallyFilled =
      formData.outletCode !== "" ||
      formData.outletName !== "" ||
      formData.bannerName !== "" ||
      formData.skus.some(sku => sku.skuCode !== "" || sku.skuName !== "") ||
      formData.installationCost > 0 ||
      formData.displayCost > 0 ||
      formData.remarks !== "" ||
      formData.blobUrls.length > 0;
  
    setHasUnsavedChanges(isPartiallyFilled);
  }, [formData, setHasUnsavedChanges]);

  return (
    <Box
      sx={{
        padding: "26px",
        border: "1px solid #EBEBEB",
        borderRadius: 2,
        overflowY: "auto",
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <GenericPopUp
        type={genericModaltype}
        message={genericModalMessage}
        openGenericModal={genericModalState}
        setOpenGenericModal={() => {
          setGenericModalState(false);
        }}
      />
      <Grid container spacing={2}>
        {/* Claim Date */}
        <CustomDateField
          label="Claim Date"
          value={formData.claimDate}
          onChange={(newValue) => handleDateChange("claimDate", newValue)}
          outerGridSize={6}
          disabled
        />

        {/* Banner Name */}
        <CustomBannerNameField
          label="Display Name"
          name="bannerName"
          value={formData.bannerName}
          onChange={handleBannerNameChange}
          required
          placeholder="Enter Display Name"
          error={errors.bannerName}
          helperText={errorMessages.bannerName}
          onBlur={validateBannerName}
          onKeyDown={validateBannerName}
        />

        {/* Outlet Code */}
        <CustomAutocompleteField
          label="Outlet Code"
          options={customersInfo}
          getOptionLabel={(option) => option.outletCode}
          value={customersInfo.find((customer) => customer.outletCode === formData.outletCode) || null}
          onChange={handleOutletChange}
          required
          placeholder="Select Outlet Code"
          error={errors.outletCode}
          helperText={errorMessages.outletCode}
          onBlur={validateOutletCode}
        />

        <CustomAutocompleteField
          label="Outlet Name"
          options={customersInfo}
          getOptionLabel={(option) => option.outletName}
          value={customersInfo.find((customer) => customer.outletName === formData.outletName) || null}
          onChange={handleOutletChange}
          required
          placeholder="Select Outlet Name"
          error={errors.outletName}
          helperText={errorMessages.outletName}
          onBlur={validateOutletName}
        />
        {/* SKU Code & Name */}
        <Grid md={12} flexDirection={"row"} display={"flex"} alignItems={"center"} padding={"8px"} gap={1}>
          <Grid container alignItems="center">
            <Grid
              item
              sx={{ width: '12.5%' }}
              xs={undefined}
            >
              <span className="display_form_labels">SKU Code & Name</span>
            </Grid>
            <Grid
              item
              sx={{ width: '87%' }}
              xs={undefined}
            >      <Box
              sx={{
                border: "1px solid #D9D9D9",
                borderRadius: "8px",
                padding: "10px",
              }}
            >
                <Grid container spacing={2}>
                  {/* First CustomSKUSelect */}
                  <Grid item xs={6}>
                    <CustomSKUSelect
                      options={skuOptions}
                      value={
                        formData.skus[0]?.skuCode
                          ? {
                            value: formData.skus[0].skuCode,
                            label: `${formData.skus[0].skuCode} - ${formData.skus[0].skuName}`,
                          }
                          : null
                      }
                      onChange={(newValue, actionMeta) => handleSKUChange(0, newValue, actionMeta)}
                      placeholder="Select SKU 1"
                    />
                  </Grid>

                  {/* Second CustomSKUSelect */}
                  <Grid item xs={6}>
                    <CustomSKUSelect
                      options={skuOptions}
                      value={
                        formData.skus[1]?.skuCode
                          ? {
                            value: formData.skus[1].skuCode,
                            label: `${formData.skus[1].skuCode} - ${formData.skus[1].skuName}`,
                          }
                          : null
                      }
                      onChange={(newValue, actionMeta) => handleSKUChange(1, newValue, actionMeta)}
                      placeholder="Select SKU 2"
                    />
                  </Grid>

                  {/* Additional CustomSKUSelect Components */}
                  {formData.skus.slice(2).map((sku, index) => (
                    <Grid item xs={6} key={index + 2}>
                      <CustomSKUSelect
                        options={skuOptions}
                        value={
                          sku.skuCode
                            ? { value: sku.skuCode, label: `${sku.skuCode} - ${sku.skuName}` }
                            : null
                        }
                        onChange={(newValue, actionMeta) => handleSKUChange(index + 2, newValue, actionMeta)}
                        placeholder={`Select SKU ${index + 3}`}
                      />
                    </Grid>
                  ))}

                  {/* Add More SKUs Button */}
                  <Grid item xs={12}>
                    <Button
                      variant="text"
                      onClick={addMoreSKUs}
                      sx={{
                        color: "#237C9D",
                        textTransform: "none",
                        margin: "0px",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      + Add More SKUs
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <CustomDateField
          label="Display Start Date"
          value={formData.displayStartDate}
          onChange={(newValue) => handleDateChange("displayStartDate", newValue)}
          required
          error={errors.displayStartDate}
          helperText={errorMessages.displayStartDate}
          onBlur={() => {
            if (!formData.displayStartDate) {
              setErrors((prev) => ({ ...prev, displayStartDate: true }));
              setErrorMessages((prev) => ({ ...prev, displayStartDate: "Display Start Date is required." }));
            }
          }}
        />

        <CustomDateField
          label="Display End Date"
          value={formData.displayEndDate}
          onChange={(newValue) => handleDateChange("displayEndDate", newValue)}
          required
          error={errors.displayEndDate}
          helperText={errorMessages.displayEndDate}
          onBlur={() => {
            if (!formData.displayEndDate) {
              setErrors((prev) => ({ ...prev, displayEndDate: true }));
              setErrorMessages((prev) => ({ ...prev, displayEndDate: "Display End Date is required." }));
            }
          }}
        />
        {/* Installation Cost */}
        <CustomCostField
          label="Installation Cost"
          name="installationCost"
          value={formData.installationCost}
          currencySymbol={currencySymbol}
          onChange={handleNumberInputChange}
          required
          placeholder="0"
          error={errors.installationCost}
          helperText={errorMessages.installationCost}
          onBlur={validateInstallationCost}
          onKeyDown={validateInstallationCost}
        />

        {/* Display Cost */}
        <CustomCostField
          label="Display Cost"
          name="displayCost"
          value={formData.displayCost}
          currencySymbol={currencySymbol}
          onChange={handleNumberInputChange}
          placeholder="0"
        />

        {/* Remarks */}
        <CustomTextAreaField
          label="Remarks"
          name="remarks"
          value={formData.remarks}
          onChange={handleInputChange}
          required
          outerGridSize={12}
          placeholder="Write Remark..."
          error={errors.remarks}
          helperText={errorMessages.remarks}
          onBlur={validateRemarks}
          onKeyDown={validateRemarks}
        />

        <CustomFileUploadField
          blobUrls={formData.blobUrls}
          onFileChange={(blobUrls) => setFormData((prev) => ({ ...prev, blobUrls }))}
          value={formData.proofOfExpenseImage}
          openPopup={openPopUp}
          error={errors.blobUrls}
          helperText={errorMessages.blobUrls}
          onBlur={validateBlobUrls}
        />

        <Grid item xs={12} md={12} padding={"0px"}>
          <span className="display_form_labels">Total Claim Amount : </span>
          <span style={{ fontWeight: 500 }}>
            {currencySymbol} {formatDecimal(formData.totalClaimAmount)}
          </span>
        </Grid>

        {/* Button Container */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              color: "#23C6B1",
              borderColor: "#23C6B1",
              textTransform: "none",
            }}
            onClick={() => handleSave(true)}
            disabled={!isFormValid()}
          >
            Save & Add Another
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#23C6B1",
              color: "#fff",
              ml: 2,
              textTransform: "none",
            }}
            onClick={() => handleSave(false)}
            disabled={!isFormValid()}
          >
            Proceed
          </Button>
        </Box>
      </Grid>
    </Box>
  );
};

export default DisplayVisibilityForm;
